import { runAppSaga } from 'app/state/store'
import { atInstallationDoInstall } from 'features/account/state/accountSagas'
import { connectionsQueryKeys } from 'features/connections/api/connectionsQueries'
import { queryClient, queryClientRemoveCacheArrayElement, queryClientUpdateCacheArrayElement } from 'queryClient'
import { useMutation, useQuery } from 'react-query'
import {
  deleteAnalyticsToolbox,
  DeleteAnalyticsToolboxParams,
  updateAtInstallationConfig,
  UpdateAtInstallationConfigParams,
  validateAtInstallationCredentials,
  ValidateAtInstallationCredentialsParams
} from '../api/integrations'
import {
  atInstallationsKeys,
  createOrUpdateBucketMutation,
  deleteBucketMutation,
  getAtInstallationQuery,
  getAtInstallationsQuery,
  getBucketQuery,
  getBucketsQuery,
  getIntegrationsExternalIdpsQuery,
  getLatestAtVersionsQuery,
  useDatabricksOAuthU2MQueries,
  useIntegrationsExternalIdpQueries,
  useWorkloadIdentityFederationQueries
} from '../api/integrationsQueries'
import { Bucket, ExternalIdp, Integration, IntegrationType } from '../types'
import { getIntegrationsCatalog } from '../ui/catalog'
import { useMemo } from 'react'
import useGetAccountData from 'features/account/ui/common/useGetAccountData'
import { FeatureFlag, useFeatureFlag } from 'app/ui/useFeatureFlag'
import settings from 'settings'

export function useIntegrations() {
  const isWifEnabled = useFeatureFlag(FeatureFlag.WorkloadIdentityFederation)
  const isDataBricksOAuthU2mEnabled = useFeatureFlag(FeatureFlag.DatabricksOAuth)
  const { tenant } = useGetAccountData()
  const queries = useWorkloadIdentityFederationQueries()
  const { data: wif, isLoading: wifLoading } = useQuery(queries.checkIntegration)
  const databricksOAuthU2MQueries = useDatabricksOAuthU2MQueries()
  const { data: dataBricksOAuthU2M, isLoading: dataBricksOAuthU2MLoading } = useQuery(
    databricksOAuthU2MQueries.checkIntegration
  )
  const { data: buckets, isLoading: areBucketsLoading } = useQuery(getBucketsQuery)
  const { data: externalIdps, isLoading: areExternalIdpsLoading } = useQuery({
    ...getIntegrationsExternalIdpsQuery,
    enabled: true
  })

  const catalog = useMemo(() => {
    const {
      [IntegrationType.BQWorkloadIdentityFederation]: wif,
      [IntegrationType.DatabricksOAuthU2M]: dataBricksOAuthU2M,
      ...integrationsCatalog
    } = getIntegrationsCatalog({
      tenant
    })

    if (settings.isSnowflakeNativeApp) {
      return {
        [IntegrationType.ExternalIdp]: integrationsCatalog[IntegrationType.ExternalIdp]
      }
    }

    let newCatalog = {
      ...integrationsCatalog,
      ...(isWifEnabled ? { [IntegrationType.BQWorkloadIdentityFederation]: wif } : {})
    }
    newCatalog = {
      ...newCatalog,
      ...(isDataBricksOAuthU2mEnabled ? { [IntegrationType.DatabricksOAuthU2M]: dataBricksOAuthU2M } : {})
    }
    return newCatalog
  }, [tenant, isWifEnabled, isDataBricksOAuthU2mEnabled])

  const items: Integration[] = Object.values(catalog).map((catalogItem) => {
    if (catalogItem.integrationType === IntegrationType.ExternalIdp) {
      return { isLoading: areExternalIdpsLoading, ...catalogItem, ...externalIdps?.[0] }
    } else if (catalogItem.integrationType === IntegrationType.BQWorkloadIdentityFederation) {
      return { isLoading: wifLoading, ...catalogItem, id: wif }
    } else if (catalogItem.integrationType === IntegrationType.DatabricksOAuthU2M) {
      return { isLoading: dataBricksOAuthU2MLoading, ...catalogItem, id: dataBricksOAuthU2M?.id }
    } else {
      const bucket = buckets?.find((d) => catalogItem.integrationType === d.integrationType)
      return { isLoading: areBucketsLoading, ...catalogItem, ...bucket }
    }
  })

  return items
}

export function useBucket(bucketId) {
  return useQuery({ ...getBucketQuery(bucketId), enabled: Boolean(bucketId) })
}

export function useCreateOrUpdateBucket() {
  return useMutation<Bucket, Error, Bucket>(createOrUpdateBucketMutation)
}

export function useDeleteBucket(bucketId: string) {
  return useMutation<{ status }, Error, void>(deleteBucketMutation(bucketId))
}

export function useAtInstallations() {
  return useQuery(getAtInstallationsQuery)
}

export function useAtInstallation(id: string | undefined) {
  return useQuery(getAtInstallationQuery(id))
}

export function useLatestAtVersions() {
  return useQuery(getLatestAtVersionsQuery)
}

export function useValidateAtInstallationCredentialsMutation() {
  return useMutation({
    mutationFn: (params: ValidateAtInstallationCredentialsParams) => validateAtInstallationCredentials(params),
    onSuccess: (data) => {
      queryClientUpdateCacheArrayElement(atInstallationsKeys.all, data)
    }
  })
}

export function usePerformAtInstallatiopnMutation() {
  return useMutation({
    mutationFn: (params: { id: string }) =>
      runAppSaga(function* () {
        const result = yield atInstallationDoInstall(params)
        return result
      }),
    onSuccess: (data) => {
      if (data) {
        queryClientUpdateCacheArrayElement(atInstallationsKeys.all, data)
      }
      queryClient.invalidateQueries(connectionsQueryKeys.all)
    }
  })
}

export function useUpdateAtInstallationCredentialsMutation() {
  return useMutation({
    mutationFn: (params: UpdateAtInstallationConfigParams) => updateAtInstallationConfig(params),
    onSuccess: (data) => {
      queryClientUpdateCacheArrayElement(atInstallationsKeys.all, data)
    }
  })
}

export function useDeleteAtInstallationMutation() {
  return useMutation({
    mutationFn: (params: DeleteAnalyticsToolboxParams) => deleteAnalyticsToolbox(params),
    onSuccess: (_data, { id }) => {
      queryClientRemoveCacheArrayElement(atInstallationsKeys.all, id)
      queryClient.invalidateQueries(connectionsQueryKeys.all)
    }
  })
}

export function useGetExternalIdps() {
  const { queries } = useIntegrationsExternalIdpQueries()
  const { data = [], ...queryResult } = useQuery(queries.getIntegrationsExternalIdps)
  return { data, ...queryResult }
}

export function useCreateExternalIdp() {
  const { queries } = useIntegrationsExternalIdpQueries()
  return useMutation<ExternalIdp, Error, ExternalIdp>(queries.createExternalIdp)
}

export function useUpdateExternalIdp() {
  const { queries } = useIntegrationsExternalIdpQueries()
  return useMutation<ExternalIdp, Error, ExternalIdp>(queries.updateExternalIdp)
}

export function useIntegrationExternalIdp(externalIdpId) {
  const { queries } = useIntegrationsExternalIdpQueries(externalIdpId)
  return useQuery(queries.getIntegrationExternalIdp)
}
